// Shared utilities for sandboxed HTML preview rendering via srcdoc iframe

// Injected into each srcdoc to report body height to the parent via postMessage.
// sandbox="allow-scripts" (no allow-same-origin) gives the iframe a null origin,
// so it cannot reach the parent's DOM, cookies, or localStorage.
const HEIGHT_SCRIPT = [
    "<script>(function(){",
    "function report(){window.parent.postMessage({type:'html-preview-height',height:document.body.scrollHeight},'*')}",
    "window.addEventListener('load',report);",
    "new ResizeObserver(report).observe(document.body);",
    "})()</script>",
].join("");

/**
 * Read all CSS custom properties from the live document and return them as a
 * :root{} block, then append generic --color-* / --font-* / --border-radius-*
 * tokens that map onto the Stekz vars. Because we read from getComputedStyle,
 * dark mode and any future theme changes are reflected automatically.
 */
export function getDocumentCSSVars(): string {
    // ── Live Stekz vars from the current document (dark-mode aware) ──────────
    const computed = getComputedStyle(document.documentElement);
    const liveDecls: string[] = [];
    for (const name of Array.from(computed)) {
        if (name.startsWith("--")) {
            const value = computed.getPropertyValue(name).trim();
            if (value) liveDecls.push(`${name}:${value}`);
        }
    }

    // ── Generic tokens that map onto the Stekz vars above ────────────────────
    const dark = document.documentElement.classList.contains("dark-mode");
    const generic: Record<string, string> = {
        // Backgrounds
        "--color-background-primary": "var(--surface-background-mild)",
        "--color-background-secondary": "var(--surface-background)",
        "--color-background-inverse": "var(--heading-color)",
        "--color-background-ghost": "transparent",
        "--color-background-success": "var(--palette-green-100)",
        "--color-background-warning": "var(--palette-orange-200)",
        "--color-background-danger": dark ? "#4a1a1a" : "#fdecea",
        "--color-background-info": dark ? "#1e2d4a" : "#d6e4f7",
        // Text
        "--color-text-primary": "var(--heading-color)",
        "--color-text-secondary": "var(--text-color)",
        "--color-text-tertiary": "var(--text-color-secondary)",
        "--color-text-inverse": "var(--stekz-white)",
        "--color-text-success": "var(--palette-green-500)",
        "--color-text-warning": "var(--palette-orange-600)",
        "--color-text-danger": dark ? "#e88080" : "#a33030",
        "--color-text-info": "var(--stekz-dark-blue)",
        "--color-text-disabled": dark
            ? "rgba(255,255,255,0.4)"
            : "rgba(102,102,102,0.4)",
        "--color-text-ghost": dark
            ? "rgba(175,175,175,0.5)"
            : "rgba(102,102,102,0.5)",
        // Borders
        "--color-border-primary": dark
            ? "rgba(224,223,223,0.4)"
            : "rgba(31,30,29,0.4)",
        "--color-border-secondary": dark
            ? "rgba(224,223,223,0.3)"
            : "rgba(31,30,29,0.3)",
        "--color-border-tertiary": dark
            ? "rgba(224,223,223,0.15)"
            : "rgba(31,30,29,0.15)",
        "--color-border-success": "var(--palette-green-500)",
        "--color-border-warning": "var(--palette-orange-400)",
        "--color-border-danger": dark ? "#e88080" : "#c04040",
        "--color-border-info": "var(--stekz-dark-blue)",
        // Brand
        "--color-brand-primary": "var(--stekz-primary-color)",
        "--color-brand-secondary": "var(--stekz-secondary-color)",
        "--color-brand-tertiary": "var(--stekz-tertiary-color)",
        "--color-brand-gradient": "var(--stekz-gradient)",
        // Typography
        "--font-sans": "var(--font-primary)",
        "--font-serif": "Georgia, serif",
        "--font-mono": "ui-monospace, monospace",
        "--font-weight-normal": "400",
        "--font-weight-medium": "500",
        "--font-weight-semibold": "600",
        "--font-weight-bold": "700",
        "--font-text-xs-size": "var(--text-xs)",
        "--font-text-sm-size": "var(--text-sm)",
        "--font-text-md-size": "var(--text-md)",
        "--font-text-lg-size": "var(--text-lg)",
        "--font-text-xl-size": "var(--text-xl)",
        "--font-heading-line-height": "var(--heading-line-height)",
        "--font-body-line-height": "var(--body-line-height)",
        // Radii
        "--border-radius-xs": "4px",
        "--border-radius-sm": "6px",
        "--border-radius-md": "8px",
        "--border-radius-lg": "10px",
        "--border-radius-xl": "12px",
        "--border-radius-full": "9999px",
        // Shadows
        "--shadow-sm":
            "0 1px 3px 0 rgba(0,0,0,0.1),0 1px 2px -1px rgba(0,0,0,0.1)",
        "--shadow-md":
            "0 4px 6px -1px rgba(0,0,0,0.1),0 2px 4px -2px rgba(0,0,0,0.1)",
        "--shadow-lg":
            "0 10px 15px -3px rgba(0,0,0,0.1),0 4px 6px -4px rgba(0,0,0,0.1)",
    };

    const genericDecls = Object.entries(generic).map(([k, v]) => `${k}:${v}`);
    return `:root{${[...liveDecls, ...genericDecls].join(";")}}`;
}

/**
 * Build the srcdoc string for a sandboxed HTML preview iframe.
 * Pass cssVars (from getDocumentCSSVars) to forward the app's design tokens.
 */
export function buildHtmlSrcdoc(source: string, cssVars = ""): string {
    return [
        "<!DOCTYPE html><html>",
        "<head><meta charset='utf-8'>",
        // Plus Jakarta Sans — matches the app's --font-primary
        "<link rel='stylesheet' href='https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&display=swap'>",
        // Tabler Icons webfont so ti-* icon classes resolve inside the iframe
        "<link rel='stylesheet' href='https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/dist/tabler-icons.min.css'>",
        // Forward parent design tokens so var(--stekz-*), var(--text-color), etc. work
        cssVars ? `<style>${cssVars}</style>` : "",
        // Base resets matching the app's typography defaults
        "<style>*{box-sizing:border-box}body{margin:0;padding:1rem;font-family:var(--font-primary,'Plus Jakarta Sans',sans-serif);font-size:var(--text-base-size,1em);color:var(--text-color,#666);line-height:var(--body-line-height,1.4);overflow:hidden}h1,h2,h3,h4{font-family:var(--font-secondary,'Plus Jakarta Sans',sans-serif);color:var(--heading-color,#27292d);line-height:var(--heading-line-height,1.2)}</style>",
        "</head><body>",
        source,
        // Tailwind MUST come after the body content so it scans a populated DOM
        "<script src='https://cdn.tailwindcss.com'></script>",
        HEIGHT_SCRIPT,
        "</body></html>",
    ].join("");
}

/**
 * Attach a window message listener that fires onHeight whenever the given
 * iframe reports its content height. Returns a cleanup function.
 */
export function createHeightMessageHandler(
    iframe: HTMLIFrameElement,
    onHeight: (height: number) => void
): () => void {
    const handler = (event: MessageEvent) => {
        if (
            event.source === iframe.contentWindow &&
            event.data?.type === "html-preview-height"
        ) {
            onHeight(event.data.height + 16);
        }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
}
